import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Mail, Calendar, FileText, MessageSquare, ArrowLeft } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { KTOfferDialog } from "./KTOfferDialog";

interface KTContactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSendContact: (email: string) => void;
  onCreateFollowUp: (email: string) => void;
  onAddNote?: (note: string) => void;
  onActivateKTTag?: () => void;
  currentEmail?: string;
  unitId?: string;
  unitName?: string;
  hasOfferSent?: boolean;
}

export function KTContactDialog({
  open,
  onOpenChange,
  onSendContact,
  onCreateFollowUp,
  onAddNote,
  onActivateKTTag,
  currentEmail = "",
  unitId,
  unitName,
  hasOfferSent = false,
}: KTContactDialogProps) {
  const [email, setEmail] = useState(currentEmail);
  const [showOfferDialog, setShowOfferDialog] = useState(false);
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [note, setNote] = useState("");

  const handleSendContact = () => {
    if (!email || !email.includes("@")) {
      toast.error("Bitte geben Sie eine gültige E-Mail-Adresse ein");
      return;
    }
    onSendContact(email);
    onOpenChange(false);
  };

  const handleCreateFollowUp = () => {
    onCreateFollowUp(email);
    onOpenChange(false);
  };

  const handleSendOffer = () => {
    if (!email || !email.includes("@")) {
      toast.error("Bitte geben Sie eine gültige E-Mail-Adresse ein");
      return;
    }
    setShowOfferDialog(true);
  };

  const handleOfferSent = (sentEmail: string) => {
    setShowOfferDialog(false);
    onOpenChange(false);
  };

  const handleAddNote = async () => {
    if (!note.trim()) {
      toast.error("Bitte geben Sie eine Notiz ein");
      return;
    }
    if (onAddNote) {
      onAddNote(note.trim());
    }
    // Activate KT tag when adding a contact note
    if (onActivateKTTag) {
      await onActivateKTTag();
    }
    setNote("");
    setShowNoteInput(false);
    onOpenChange(false);
    toast.success("Notiz hinzugefügt & KT-Tag gesetzt");
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>KT - Kontakt / Termin</DialogTitle>
            <DialogDescription>
              {showNoteInput ? "Notiz zum Kontakt hinzufügen" : "E-Mail-Adresse eingeben und Aktion wählen"}
            </DialogDescription>
          </DialogHeader>
          
          {showNoteInput ? (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="note">Kontakt-Notiz</Label>
                <Textarea
                  id="note"
                  placeholder="z.B. Kunde möchte Rückruf am Nachmittag..."
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  className="min-h-[100px]"
                  autoFocus
                />
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => setShowNoteInput(false)}
                  variant="outline"
                  className="flex-1"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Zurück
                </Button>
                <Button
                  onClick={handleAddNote}
                  className="flex-1"
                  disabled={!note.trim()}
                >
                  <MessageSquare className="mr-2 h-4 w-4" />
                  Notiz speichern
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="email">E-Mail-Adresse</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="kunde@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="flex flex-col gap-2">
                {!hasOfferSent && (
                  <Button
                    onClick={handleSendOffer}
                    className="w-full"
                    variant="default"
                  >
                    <FileText className="mr-2 h-4 w-4" />
                    Angebot versenden
                  </Button>
                )}
                <Button
                  onClick={handleSendContact}
                  className="w-full"
                  variant="outline"
                >
                  <Mail className="mr-2 h-4 w-4" />
                  Kontaktdaten senden
                </Button>
                <Button
                  onClick={handleCreateFollowUp}
                  className="w-full"
                  variant="outline"
                >
                  <Calendar className="mr-2 h-4 w-4" />
                  Follow-up erstellen
                </Button>
                <Button
                  onClick={() => setShowNoteInput(true)}
                  className="w-full"
                  variant="outline"
                >
                  <MessageSquare className="mr-2 h-4 w-4" />
                  Kontakt-Notiz
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {showOfferDialog && unitId && (
        <KTOfferDialog
          open={showOfferDialog}
          onOpenChange={setShowOfferDialog}
          unitId={unitId}
          unitName={unitName}
          currentEmail={email}
          onOfferSent={handleOfferSent}
        />
      )}
    </>
  );
}
